import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Alert, IconCheckmarkCircleOutline, List, LoadingMask, Spacer } from '@zillow/constellation';
import { Button, ButtonGroup, Heading, Image, Modal, Paragraph, TextButton } from '@zillow/constellation-10';

import PropertiesActions from '../../../actions/PropertiesActions';
import { PropertiesSelectors } from '../../../selectors';
import WaitlistActions from '../../../actions/WaitlistActions';
import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import ForSaleSign from '../../../images/addPropertyPage/ForSaleSign.svg';
import KeyHome from '../../../images/KeyHome.svg';
import { leftAlignedTextStyles } from './StyledComponents';

const waitlistActionsCopy = {
  start: {
    image: KeyHome,
    heading: 'Start a waitlist?',
    title: 'Get leads next time you list your property',
    details: [
      { message: 'The listing will remain active, but the property will show as unavailable.', showUnpaid: true },
      {
        message:
          "Renters can indicate interest, and you'll receive their contact information when you end the waitlist.",
        showUnpaid: true,
      },
      {
        message: 'Because the listing will remain active, the monthly fee for your package tier will still apply.',
        showUnpaid: false,
      },
    ],
    footer: 'Still have questions? Please contact your Zillow representative.',
  },
  end: {
    image: ForSaleSign,
    heading: 'End the waitlist?',
    title: 'When you end the waitlist',
    details: [
      { message: 'The listing will show as an available property on Zillow, Trulia & HotPads.', showUnpaid: true },
      {
        message: "You'll receive the list of any renters who showed interest in the property while it was unavailable.",
        showUnpaid: true,
      },
    ],
  },
};

export const WaitlistModal = ({ setShowWaitlistModal }) => {
  const dispatch = useDispatch();

  const { listingAlias, isWaitlisted, customerId, syndication, adproduct } = useSelector((state) =>
    PropertiesSelectors.makeCurrentProperty(state),
  );

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [action] = useState(isWaitlisted ? 'end' : 'start');

  const submitWaitlist = () => {
    setLoading(true);
    if (action === 'end') {
      dispatch(WaitlistActions.endWaitlist(listingAlias, adproduct.isUnpaid ? adproduct.unpaidCustomerId : customerId))
        .then(() => {
          const updatedValues = {
            isWaitlisted: false,
            syndication: {
              ...syndication,
              overview: {
                ...syndication.overview,
                displayStatus: PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHED,
                status: PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHED,
              },
            },
          };
          dispatch(PropertiesActions.updateCurrentProperty(updatedValues));
          setShowWaitlistModal(false);
        })
        .catch(() => {
          setLoading(false);
          setError(ErrorMessageConstants.DEFAULT_ERROR);
        });
    } else {
      dispatch(
        WaitlistActions.startWaitlist(listingAlias, adproduct.isUnpaid ? adproduct.unpaidCustomerId : customerId),
      )
        .then(() => {
          const updatedValues = {
            isWaitlisted: true,
            syndication: {
              ...syndication,
              overview: {
                ...syndication.overview,
                displayStatus: PropertyConstants.SYNDICATION_OVERVIEW_STATUS.WAITLISTED,
                status: PropertyConstants.SYNDICATION_OVERVIEW_STATUS.WAITLISTED,
              },
            },
          };

          dispatch(PropertiesActions.updateCurrentProperty(updatedValues));
          setLoading(false);
          setStep(2);
        })
        .catch(() => {
          setLoading(false);
          setError(ErrorMessageConstants.DEFAULT_ERROR);
        });
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && setShowWaitlistModal(false)}
      header={<Heading level={2}>{waitlistActionsCopy[action].heading}</Heading>}
      body={
        <div data-testid="WaitlistModal">
          {error && (
            <Spacer marginBottom="sm">
              <Alert appearance="error" body={error} marginBottom="sm" />
            </Spacer>
          )}

          {step === 1 ? (
            <LoadingMask loading={loading}>
              <Spacer marginBottom="md">
                <Image alt="" src={waitlistActionsCopy[action].image} />
              </Spacer>
              <Heading level="4" fontFamily="serif" marginBottom="sm">
                {waitlistActionsCopy[action].title}
              </Heading>
              <List css={leftAlignedTextStyles}>
                {waitlistActionsCopy[action].details.map(
                  (detail, index) =>
                    (!adproduct.isUnpaid || detail.showUnpaid) && (
                      <List.Item key={`${action}Detail${index}`}>{detail.message}</List.Item>
                    ),
                )}
              </List>
              {waitlistActionsCopy[action].footer && (
                <Paragraph css={{ marginTop: '600', marginBottom: '800' }}>
                  {waitlistActionsCopy[action].footer}
                </Paragraph>
              )}
            </LoadingMask>
          ) : (
            step === 2 &&
            action === 'start' && (
              <>
                <Spacer marginBottom="md">
                  <IconCheckmarkCircleOutline fontColor="brand" size="lg" />
                </Spacer>
                <Heading level="4" fontFamily="serif" marginBottom="sm">
                  Your property will show as unavailable
                </Heading>
                <Paragraph css={{ marginBottom: '600' }}>
                  {/* eslint-disable-next-line max-len */}
                  Renters interested in the property will be saved for you until the end of the waitlist.
                </Paragraph>
                <Paragraph>{waitlistActionsCopy.start.footer}</Paragraph>
              </>
            )
          )}
        </div>
      }
      footer={
        step === 2 ? (
          <Button onClick={() => setShowWaitlistModal(false)} tone="brand" emphasis="filled" type="button">
            OK
          </Button>
        ) : (
          <ButtonGroup aria-label="Waitlist action buttons">
            <TextButton onClick={() => setShowWaitlistModal(false)} type="button">
              Cancel
            </TextButton>
            <Button tone="brand" emphasis="filled" size="sm" onClick={submitWaitlist} type="button">
              {`Yes, ${action === 'start' ? 'start' : 'end'} waitlist`}
            </Button>
          </ButtonGroup>
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

WaitlistModal.propTypes = {
  setShowWaitlistModal: PropTypes.func,
};

WaitlistModal.defaultProps = {
  setShowWaitlistModal: () => {},
};

export default WaitlistModal;
